/**
 * profileCompletion.js
 *
 * Pure utility — calculate transporter profile completion percentage.
 * Returns { percentage: number, breakdown: Array<{ field, label, weight, filled, earned }> }
 *
 * Usage:
 *   import { calculateProfileCompletion } from '../shared_utilities/profileCompletion';
 *   const { percentage, breakdown } = calculateProfileCompletion(transporterProfile);
 */

// ── Weightage table (source: LoRRI product spec) ───────────────────────────
// 'license' is excluded as per spec (marked '-')
const FIELD_WEIGHTS = [
    { field: 'transporter_name', label: 'Company Name', weight: 2 },
    { field: 'website', label: 'Website', weight: 2 },
    { field: 'company_type', label: 'Company Type', weight: 1 },
    { field: 'headquarter', label: 'Headquarters', weight: 1 },
    { field: 'established', label: 'Established Year', weight: 1 },
    { field: 'zone', label: 'Area of Operation', weight: 5 },
    { field: 'turnover', label: 'Turnover', weight: 4 },
    { field: 'net_profit', label: 'Net Profit', weight: 2 },
    { field: 'owned_fleet', label: 'Owned Fleet', weight: 3 },
    { field: 'contact_information', label: 'Contact Information', weight: 5 },
    { field: 'attachments', label: 'Attachments', weight: 3 },
    { field: 'modes_of_transportation', label: 'Modes of Transportation', weight: 4 },
    { field: 'about_us', label: 'About Us', weight: 3 },
    { field: 'modes_of_operation', label: 'Modes of Operation', weight: 2 },
    { field: 'industry_served', label: 'Industry Served', weight: 5 },
    { field: 'warehousing_services', label: 'Warehousing Services', weight: 2 },
    { field: 'value_added_services', label: 'Value-Added Services', weight: 3 },
    { field: 'gst', label: 'GST Details', weight: 1 },
    { field: 'shipment_tracking_method', label: 'Shipment Tracking', weight: 3 },
    { field: 'bank_details', label: 'Bank Details', weight: 1 },
    { field: 'manpower_details', label: 'Manpower Details', weight: 2 },
    { field: 'acceptable_terms', label: 'Acceptable Terms', weight: 3 },
    { field: 'about_us', label: 'Company Description', weight: 4, _alias: 'company_details' },
    { field: 'customer_reference', label: 'Customer References', weight: 9 },
    { field: 'top_5_companies', label: 'Top 5 Companies', weight: 5 },
    { field: 'other_business_interest', label: 'Other Business Interest', weight: 1 },
    { field: 'logo', label: 'Company Logo', weight: 2 },
    { field: 'truck_asset_details', label: 'Truck Asset Details', weight: 5 },
    { field: 'authSig_details', label: 'Authorised Signatory', weight: 2 },
    { field: 'company_act', label: 'Company Act', weight: 1 },
    { field: 'contract_attachments', label: 'Contract Attachments', weight: 4 },
    { field: 'associated_fleet', label: 'Associated Fleet', weight: 2 },
    { field: 'experience_year', label: 'Years of Experience', weight: 1 },
    { field: 'domain_business', label: 'Domain Business', weight: 1 },
    { field: 'tms_experience', label: 'TMS Experience', weight: 2 },
    { field: 'number_of_ratings', label: 'Number of Ratings', weight: 3 },
];

// De-duplicate (about_us appears twice for different display labels; pick highest)
// Use a Map keyed on field to avoid double-counting
const DEDUPLICATED_WEIGHTS = (() => {
    const map = new Map();
    FIELD_WEIGHTS.forEach(fw => {
        const key = fw.field;
        if (!map.has(key) || fw.weight > map.get(key).weight) {
            map.set(key, fw);
        }
    });
    return Array.from(map.values());
})();

const TOTAL_WEIGHT = DEDUPLICATED_WEIGHTS.reduce((sum, fw) => sum + fw.weight, 0);

// ── Field "filled" checkers ────────────────────────────────────────────────

/**
 * Determine whether a profile value counts as "filled".
 * Handles strings, arrays, and nested objects.
 */
const isFilled = (value) => {
    if (value === null || value === undefined) return false;

    if (typeof value === 'string') return value.trim().length > 0;

    if (typeof value === 'number') return true; // 0 is still a value

    if (Array.isArray(value)) {
        if (value.length === 0) return false;
        // At least one item must be non-trivially filled
        return value.some(item => isFilled(item));
    }

    if (typeof value === 'object') {
        // Check if any own property has a meaningful value
        return Object.values(value).some(v => {
            if (v === null || v === undefined || v === '') return false;
            if (typeof v === 'string') return v.trim().length > 0;
            if (Array.isArray(v)) return v.length > 0;
            return true;
        });
    }

    return Boolean(value);
};

// ── Main export ────────────────────────────────────────────────────────────

/**
 * calculateProfileCompletion
 *
 * @param {object} profile - the transporter profile object
 * @returns {{ percentage: number, breakdown: Array, totalWeight: number }}
 */
export const calculateProfileCompletion = (profile) => {
    if (!profile || typeof profile !== 'object') {
        return { percentage: 0, breakdown: [], totalWeight: TOTAL_WEIGHT };
    }

    let earnedWeight = 0;
    const breakdown = DEDUPLICATED_WEIGHTS.map(fw => {
        const value = profile[fw.field];
        const filled = isFilled(value);
        const earned = filled ? fw.weight : 0;
        earnedWeight += earned;

        return {
            field: fw.field,
            label: fw.label,
            weight: fw.weight,
            filled,
            earned,
        };
    });

    // Normalise to 100% (TOTAL_WEIGHT is the denominator)
    const percentage = Math.min(100, Math.round((earnedWeight / TOTAL_WEIGHT) * 100));

    return { percentage, breakdown, totalWeight: TOTAL_WEIGHT };
};

/**
 * getCompletionColor
 * Returns a CSS color string appropriate for a gauge/ring based on the percentage.
 */
export const getCompletionColor = (pct) => {
    if (pct >= 80) return '#27ae60';  // green
    if (pct >= 50) return '#e8a600';  // amber
    return '#e03636';                 // red
};

/**
 * getMissingFields
 * Returns only the unfilled breakdown items, sorted by weight desc (highest value first).
 */
export const getMissingFields = (breakdown) => {
    return breakdown
        .filter(item => !item.filled)
        .sort((a, b) => b.weight - a.weight);
};
